import { useEffect, useState, type FormEvent } from 'react';
import { Eye, EyeOff } from 'lucide-react';

import { ApiError, authApi, MIN_PASSWORD_LENGTH, type PasswordLink } from '../lib/api';
import { Logotype } from '../components/Wordmark';

/**
 * Where the links in reset and invitation emails land.
 *
 * Shown whether or not this browser is signed in — the link is about an
 * account, not about whoever happens to be using the device — and it signs
 * nobody in. Setting the password revokes every session the account had, and
 * the person then signs in like anyone else, second factor included.
 */

export const SET_PASSWORD_PATH = '/set-password';

const TOKEN_KEY = 'qs-password-link';
const NO_LINK =
  'This page needs the link from your email. Open it again, or ask for a new one from the sign-in page.';

/**
 * Takes the token out of the link's fragment and out of the address bar, where
 * it would otherwise sit in history and in screenshots. It is kept for this tab
 * only, because iOS discards a backgrounded web app freely and a reload halfway
 * through choosing a password should not cost the link.
 */
function takeToken(): string | null {
  const fromLink = new URLSearchParams(window.location.hash.slice(1)).get('token');

  if (fromLink) {
    try {
      sessionStorage.setItem(TOKEN_KEY, fromLink);
    } catch {
      // Private browsing. The token still works for this page load.
    }
    window.history.replaceState(null, '', SET_PASSWORD_PATH);
    return fromLink;
  }

  try {
    return sessionStorage.getItem(TOKEN_KEY);
  } catch {
    return null;
  }
}

function forgetToken(): void {
  try {
    sessionStorage.removeItem(TOKEN_KEY);
  } catch {
    // Nothing was stored.
  }
}

type Step =
  | { name: 'checking' }
  | { name: 'dead'; message: string }
  | { name: 'choose'; link: PasswordLink }
  | { name: 'done'; link: PasswordLink };

export function SetPassword(): React.JSX.Element {
  const [token] = useState(takeToken);
  const [step, setStep] = useState<Step>(() =>
    token ? { name: 'checking' } : { name: 'dead', message: NO_LINK },
  );
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;

    authApi
      .describeLink(token)
      .then((link) => {
        if (!cancelled) setStep({ name: 'choose', link });
      })
      .catch((caught: unknown) => {
        if (cancelled) return;
        // A 400 means the link is dead for good. Anything else — offline,
        // mostly — is worth a reload, so the token is kept for that.
        if (caught instanceof ApiError && caught.status === 400) forgetToken();
        setStep({
          name: 'dead',
          message: caught instanceof ApiError ? caught.message : 'Something went wrong. Try again.',
        });
      });

    return () => {
      cancelled = true;
    };
  }, [token]);

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (step.name !== 'choose' || !token) return;

    if (password.length < MIN_PASSWORD_LENGTH) {
      setError(`Use at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }

    setError(null);
    setBusy(true);

    try {
      await authApi.setPassword(token, password);
      forgetToken();
      setStep({ name: 'done', link: step.link });
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Something went wrong. Try again.');
    } finally {
      setBusy(false);
    }
  }

  const invited = (step.name === 'choose' || step.name === 'done') && step.link.purpose === 'invite';

  const heading =
    step.name === 'checking'
      ? 'Checking your link…'
      : step.name === 'dead'
        ? 'This link no longer works'
        : step.name === 'done'
          ? 'Password set'
          : invited
            ? 'Choose a password'
            : 'Choose a new password';

  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper px-4 py-10">
      <div className="w-full max-w-[22rem]">
        <div className="mb-7 text-center">
          <Logotype size="lg" />
          <p className="qs-eyebrow mt-2">Cloud</p>
        </div>

        <div className="qs-panel overflow-hidden p-0">
          <div className="qs-brand-rule h-1" aria-hidden />
          <div className="p-5">
            <h1 className="text-lg font-medium tracking-tight">{heading}</h1>

            {step.name === 'dead' && (
              <>
                <p className="mt-2 text-sm text-ink-muted">{step.message}</p>
                <a
                  href="/forgot-password"
                  className="qs-brand-rule mt-5 block w-full rounded px-3 py-2 text-center font-medium text-[#080d16] transition-opacity hover:opacity-90"
                >
                  Ask for a new link
                </a>
              </>
            )}

            {step.name === 'done' && (
              <>
                <p className="mt-2 text-sm text-ink-muted">
                  Sign in with <span className="wrap-anywhere text-ink">{step.link.email}</span>{' '}
                  and your new password.
                  {!invited && ' Every device that was signed in has been signed out.'}
                </p>
                {/* A full load rather than state: the session cookie was
                    cleared, and the app should start again from /auth/me. */}
                <a
                  href="/"
                  className="qs-brand-rule mt-5 block w-full rounded px-3 py-2 text-center font-medium text-[#080d16] transition-opacity hover:opacity-90"
                >
                  Go to sign in
                </a>
              </>
            )}

            {step.name === 'choose' && (
              <form onSubmit={handleSubmit} className="mt-1 space-y-3">
                <p className="text-xs text-ink-muted">
                  {invited ? `Welcome, ${step.link.fullName}. ` : ''}
                  For <span className="wrap-anywhere text-ink">{step.link.email}</span>
                </p>

                {/* For password managers: without a username in the form, a
                    saved password is filed against nothing and never offered
                    at sign-in. */}
                <input
                  type="email"
                  name="username"
                  autoComplete="username"
                  value={step.link.email}
                  readOnly
                  tabIndex={-1}
                  aria-hidden
                  className="sr-only"
                />

                <Field label="New password" htmlFor="new-password">
                  <div className="relative">
                    <input
                      id="new-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      className="w-full rounded border border-rule bg-surface py-2 pr-10 pl-2.5 outline-none focus:border-accent"
                      autoComplete="new-password"
                      minLength={MIN_PASSWORD_LENGTH}
                      autoFocus
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((shown) => !shown)}
                      tabIndex={-1}
                      aria-pressed={showPassword}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      className="qs-icon-button absolute inset-y-0 right-0 w-10 rounded text-ink-faint hover:text-ink"
                    >
                      {showPassword ? (
                        <EyeOff size={16} aria-hidden />
                      ) : (
                        <Eye size={16} aria-hidden />
                      )}
                    </button>
                  </div>
                  <p className="mt-1 text-2xs text-ink-faint">
                    At least {MIN_PASSWORD_LENGTH} characters. A few unrelated words are easy to
                    remember and hard to guess.
                  </p>
                </Field>

                <Field label="Type it again" htmlFor="confirm-password">
                  <input
                    id="confirm-password"
                    type={showPassword ? 'text' : 'password'}
                    value={confirm}
                    onChange={(event) => setConfirm(event.target.value)}
                    className="w-full rounded border border-rule bg-surface px-2.5 py-2 outline-none focus:border-accent"
                    autoComplete="new-password"
                    required
                  />
                </Field>

                {error && (
                  <p role="alert" aria-live="polite" className="text-xs text-down">
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={busy}
                  className="qs-brand-rule w-full rounded px-3 py-2 font-medium text-[#080d16] transition-opacity hover:opacity-90 disabled:opacity-60"
                >
                  {busy ? 'Saving…' : 'Set password'}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({
  label,
  htmlFor,
  children,
}: {
  label: string;
  htmlFor: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div>
      <label htmlFor={htmlFor} className="qs-eyebrow mb-1 block">
        {label}
      </label>
      {children}
    </div>
  );
}
